import { useCallback, useEffect, useRef, useState } from 'react';
import { BOOT_INITIAL_STATUS, BOOT_STATUSES, BOOT_TIMING, bootLines, bootProgress } from 'lib/bootSequence.mjs';
import { Button } from 'components/common';

/**
 * The V2/V3 boot sequence — the full-screen "operative console" intro.
 *
 * Three phases: a progress bar with status lines, a typed terminal, then a fake
 * unlock and ACCESS GRANTED before the warp-out.
 *
 * Two things differ from the original, both deliberate:
 *
 *   · The terminal was built with innerHTML, which made the operative's name
 *     live markup. It is a React child here, so it is escaped.
 *   · Every timer is owned by the effect and cleared on unmount. The original
 *     left intervals running if the user navigated mid-sequence.
 *
 * The watchdog is kept exactly as it was. This overlay covers the entire app,
 * so a phase that throws must never be able to strand someone behind it.
 */
export default function BootSequence({ name, fast = false, onDone }) {
  const [pct, setPct] = useState(0);
  const [status, setStatus] = useState(BOOT_INITIAL_STATUS);
  const [phase, setPhase] = useState('bar');
  const [typed, setTyped] = useState([]);
  const [current, setCurrent] = useState('');
  const [dots, setDots] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [granted, setGranted] = useState(false);
  const [flying, setFlying] = useState(false);
  const [warping, setWarping] = useState(false);

  const sparkRefs = [useRef(null), useRef(null), useRef(null)];
  const timers = useRef([]);
  const finished = useRef(false);

  const after = useCallback((ms, fn) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  }, []);

  const teardown = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    for (const t of timers.current) clearTimeout(t);
    timers.current = [];
    setWarping(true);
    setTimeout(() => onDone?.(), fast ? BOOT_TIMING.fastWarpMs : BOOT_TIMING.warpMs);
  }, [onDone, fast]);

  // The three sparkline canvases, drawn once.
  useEffect(() => {
    const strokes = ['rgba(0,255,156,0.7)', 'rgba(34,211,238,0.7)', 'rgba(255,61,181,0.6)'];
    sparkRefs.forEach((ref, si) => {
      const c = ref.current;
      if (!c) return;
      c.width = 80;
      c.height = 28;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      const pts = Array.from({ length: 12 }, () => 4 + Math.random() * 20);
      ctx.clearRect(0, 0, 80, 28);
      ctx.strokeStyle = strokes[si];
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      pts.forEach((y, i) => (i === 0 ? ctx.moveTo(0, y) : ctx.lineTo(i * (80 / 11), y)));
      ctx.stroke();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const watchdog = setTimeout(teardown, BOOT_TIMING.watchdogMs);
    timers.current.push(watchdog);

    // A restored session skips the theatre and just confirms.
    if (fast) {
      setPct(100);
      setStatus('SESSION RESTORED');
      after(BOOT_TIMING.fastMs, teardown);
      return () => { for (const t of timers.current) clearTimeout(t); timers.current = []; };
    }

    const steps = bootProgress();
    steps.forEach((p, i) => {
      after(BOOT_TIMING.tickMs * (i + 1), () => {
        setPct(p);
        setStatus(BOOT_STATUSES[Math.min(i, BOOT_STATUSES.length - 1)]);
      });
    });

    const lines = bootLines(name);
    after(BOOT_TIMING.terminalStartMs, () => {
      setPhase('terminal');
      let lineIdx = 0;
      let charIdx = 0;
      let buffer = '';

      const typeNext = () => {
        if (lineIdx >= lines.length) {
          after(150, startPhase3);
          return;
        }
        const line = lines[lineIdx];
        if (charIdx < line.length) {
          buffer += line[charIdx++];
          setCurrent(buffer);
          after(BOOT_TIMING.charMinMs + Math.random() * BOOT_TIMING.charJitterMs, typeNext);
          return;
        }
        const done = buffer;
        setTyped(prev => [...prev, done]);
        setCurrent('');
        lineIdx++;
        charIdx = 0;
        buffer = '';
        after(BOOT_TIMING.lineGapMs, typeNext);
      };

      /*
       * Four beats, in the original's order: dots fill, a pause, the padlock
       * opens, another pause, then the stamp lands together with the
       * fly-through rings. Firing the lock and the stamp on the same tick — as
       * the first port did — loses the moment the whole sequence builds to.
       */
      const startPhase3 = () => {
        setPhase('granted');
        let dc = 0;
        const tick = () => {
          dc++;
          setDots('•'.repeat(dc));
          if (dc < BOOT_TIMING.dotCount) { after(BOOT_TIMING.dotMs, tick); return; }

          after(BOOT_TIMING.unlockMs, () => {
            setUnlocked(true);
            after(BOOT_TIMING.stampMs, () => {
              setGranted(true);
              setFlying(true);   // rings expand outward as the stamp lands
              after(BOOT_TIMING.grantedHoldMs, teardown);
            });
          });
        };
        after(BOOT_TIMING.dotMs, tick);
      };

      typeNext();
    });

    return () => { for (const t of timers.current) clearTimeout(t); timers.current = []; };
  }, [name, fast, after, teardown]);

  return (
    <>
      <Button id="hax-skip" variant="skip" onClick={teardown}>SKIP ►</Button>
      <div
        id="hax-boot"
        className={`hax-boot--on${warping ? ' hax-warp' : ''}`}
        onClick={teardown}
        role="presentation"
      >
        <div className="hax-logo" id="hax-logo-txt">AFFILIATE://OPS</div>
        <div className="hax-sub">OPERATIVE CONSOLE · SECURE NODE</div>

        <div className="hax-progress-wrap">
          <div
            className={`hax-progress-bar hax-progress-bar--${fast ? 'sweep' : 'live'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="hax-status">{status}</div>

        {phase === 'bar' && (
          <>
            <div className="hax-radar-wrap">
              <svg viewBox="0 0 80 80" fill="none" aria-hidden="true">
                <circle cx="40" cy="40" r="36" stroke="rgba(0,255,156,0.15)" strokeWidth="1" />
                <circle cx="40" cy="40" r="24" stroke="rgba(0,255,156,0.1)" strokeWidth="1" />
                <circle cx="40" cy="40" r="12" stroke="rgba(0,255,156,0.1)" strokeWidth="1" />
                <line x1="40" y1="4" x2="40" y2="76" stroke="rgba(0,255,156,0.08)" strokeWidth="1" />
                <line x1="4" y1="40" x2="76" y2="40" stroke="rgba(0,255,156,0.08)" strokeWidth="1" />
                <g className="hax-radar-line">
                  <line x1="40" y1="40" x2="40" y2="4" stroke="rgba(0,255,156,0.7)" strokeWidth="1.5" />
                </g>
                <circle cx="55" cy="28" r="2" fill="#00ff9c" opacity="0.9" />
                <circle cx="30" cy="55" r="1.5" fill="#22d3ee" opacity="0.7" />
              </svg>
            </div>
            <div className="hax-sparks">
              {sparkRefs.map((ref, i) => (
                <canvas className="hax-spark" ref={ref} key={i} aria-hidden="true" />
              ))}
            </div>
          </>
        )}

        {phase === 'terminal' && (
          <div className="hax-terminal hax-terminal--on">
            {typed.map((l, i) => <div className="hax-terminal__done" key={i}>{l}</div>)}
            <div>{current}<span className="hax-cursor" /></div>
          </div>
        )}

        {phase === 'granted' && (
          <div className="hax-phase3 hax-phase3--on">
            <div className="hax-fake-pw">
              <span className="hax-lock">{unlocked ? '🔓' : '🔒'}</span>
              <span>{dots}</span>
            </div>
            {granted && <div className="hax-granted">ACCESS GRANTED</div>}
          </div>
        )}
        <div id="hax-flythrough" className={flying ? 'active' : undefined} aria-hidden="true">
          <svg viewBox="0 0 600 600" fill="none">
            <circle cx="300" cy="300" r="280" stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="20 10" opacity="0.9" />
            <circle cx="300" cy="300" r="220" stroke="#38bdf8" strokeWidth="1" strokeDasharray="10 8" opacity="0.8" />
            <circle cx="300" cy="300" r="160" stroke="#22d3ee" strokeWidth="0.8" strokeDasharray="6 6" opacity="0.7" />
            <circle cx="300" cy="300" r="100" stroke="#38bdf8" strokeWidth="0.6" opacity="0.6" />
            <circle cx="300" cy="300" r="50" stroke="#22d3ee" strokeWidth="0.5" opacity="0.5" />
          </svg>
        </div>
      </div>
    </>
  );
}
